// src/services/threadService.ts
// Domain-specific service for thread operations

import { apiClient } from "./api";
import { API_ENDPOINTS } from "../constants/index";
import type { RequestParams } from "./api";
import type { Thread } from "../types/entities";

// Type definitions
export type { Thread };

/** `ThreadBase` (POST /threads), less the owner the service adds. */
export interface CreateThreadData {
  title: string;
  image_data: string;
  group_id?: number | null;
}

/** `ThreadPatch` (PATCH /threads/{id}): only the fields to change. */
export interface UpdateThreadData {
  title?: string;
  image_data?: string;
  group_id?: number | null;
}

/**
 * Service for thread operations
 */
export class ThreadService {
  /**
   * Get all threads for a user
   */
  async getThreads(userEmail: string | null = null): Promise<Thread[]> {
    const params: RequestParams = {};
    if (userEmail) {
      params.owner_email = userEmail;
    }
    return await apiClient.get<Thread[]>(API_ENDPOINTS.THREADS, params);
  }

  /**
   * Get threads by group
   */
  async getThreadsByGroup(groupId: number): Promise<Thread[]> {
    return await apiClient.get<Thread[]>(
      `${API_ENDPOINTS.GROUPS}${groupId}/threads`,
    );
  }

  /**
   * Get a specific thread
   */
  async getThread(threadId: number): Promise<Thread> {
    return await apiClient.get<Thread>(`${API_ENDPOINTS.THREADS}${threadId}`);
  }

  /**
   * Create a new thread
   */
  async createThread(
    threadData: CreateThreadData,
    userEmail: string,
  ): Promise<Thread> {
    // Remove group_id from the data - linking happens separately
    const { group_id, ...cleanThreadData } = threadData;

    const processedData = {
      ...cleanThreadData,
      owner_email: userEmail,
    };

    const newThread = await apiClient.post<Thread>(
      API_ENDPOINTS.THREADS,
      processedData,
    );

    return newThread;
  }

  /**
   * Update an existing thread
   */
  async updateThread(
    threadId: number,
    threadData: UpdateThreadData,
  ): Promise<Thread> {
    const updatedThread = await apiClient.patch<Thread>(
      `${API_ENDPOINTS.THREADS}${threadId}`,
      threadData,
    );

    return updatedThread;
  }

  /**
   * Delete a thread
   */
  async deleteThread(threadId: number): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.THREADS}${threadId}`);

  }

  /**
   * Link a thread to a group
   */
  async linkThreadToGroup(threadId: number, groupId: number): Promise<Thread> {
    return await apiClient.patch<Thread>(
      `${API_ENDPOINTS.THREADS}${threadId}`,
      {
        group_id: groupId,
      },
    );
  }

  /**
   * Unlink a thread from a group
   */
  async unlinkThreadFromGroup(threadId: number): Promise<Thread> {
    return await apiClient.patch<Thread>(
      `${API_ENDPOINTS.THREADS}${threadId}`,
      {
        group_id: null,
      },
    );
  }
}

// Export singleton instance
export const threadService = new ThreadService();
